import { QueryClient } from '@tanstack/react-query'
import type { DefaultOptions } from '@tanstack/react-query'

// Different cache strategies for different types of data
const queryDefaults: DefaultOptions = {
  queries: {
    // Default stale time: 5 minutes
    staleTime: 5 * 60 * 1000,
    // Default garbage collection time: 10 minutes
    gcTime: 10 * 60 * 1000,
    // Retry failed requests with exponential backoff
    retry: (failureCount, error: Error) => {
      // Don't retry on authentication errors
      const statusError = error as Error & { status?: number }
      if (statusError?.status === 401 || statusError?.status === 403) {
        return false
      }
      // Don't retry more than 3 times
      return failureCount < 3
    },
    // Retry delay with jitter to avoid thundering herd
    retryDelay: (attemptIndex) => {
      const baseDelay = Math.min(1000 * 2 ** attemptIndex, 30000)
      const jitter = Math.random() * 1000
      return baseDelay + jitter
    },
    // Network mode for better offline handling
    networkMode: 'online',
    // Refetch behavior
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  },
  mutations: {
    retry: 1,
    networkMode: 'online',
  },
}

// Create a client with optimized defaults
export const queryClient = new QueryClient({
  defaultOptions: queryDefaults,
})

// Cache configuration for different data types
export const cacheConfig = {
  // Real-time data (frequently changing)
  realtime: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 60 * 1000, // 1 minute
  },
  // Dynamic data (bookings, payments)
  dynamic: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  },
  // Semi-static data (members, facilities)
  semiStatic: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },
  // Static data (settings, templates)
  static: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  },
}

// Query keys for consistent cache management
export const queryKeys = {
  // Dashboard stats
  dashboardStats: ['dashboard', 'stats'] as const,
  todaysBookings: ['bookings', 'today'] as const,
  facilityUtilization: ['facilities', 'utilization'] as const,
  revenueToday: ['revenue', 'today'] as const,
  outstandingPayments: ['payments', 'outstanding'] as const,
  
  // Calendar
  calendarBookings: ['calendar', 'bookings'] as const,
  calendarBookingsByRange: (start: string, end: string) => ['calendar', 'bookings', start, end] as const,
  
  // Members
  members: ['members'] as const,
  member: (id: string) => ['members', id] as const,
  memberStats: ['members', 'stats'] as const,
  
  // Bookings
  bookings: ['bookings'] as const,
  booking: (id: string) => ['bookings', id] as const,
  bookingsByDate: (date: string) => ['bookings', 'date', date] as const,
  
  // Facilities
  facilities: ['facilities'] as const,
  facility: (id: string) => ['facilities', id] as const,
  
  // Invoices and Payments
  invoices: ['invoices'] as const,
  invoice: (id: string) => ['invoices', id] as const,
  payments: ['payments'] as const,
  payment: (id: string) => ['payments', id] as const,
  
  // Reports and Analytics
  reports: ['reports'] as const,
  revenueReport: (period: string) => ['reports', 'revenue', period] as const,
  utilizationReport: (period: string) => ['reports', 'utilization', period] as const,
  
  // Communications
  communications: ['communications'] as const,
  communicationHistory: ['communications', 'history'] as const,
  
  // Booking participants and waitlist
  bookingParticipants: ['bookingParticipants'] as const,
  bookingWaitlist: ['bookingWaitlist'] as const,
  
  // Budget categories
  budgetCategories: ['budgetCategories'] as const,
} as const

// Prefetching utilities for better performance
export const prefetchQueries = {
  // Prefetch dashboard data on app load
  dashboardData: async () => {
    const promises = [
      queryClient.prefetchQuery({
        queryKey: queryKeys.dashboardStats,
        ...cacheConfig.realtime,
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.todaysBookings,
        ...cacheConfig.dynamic,
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.facilityUtilization,
        ...cacheConfig.semiStatic,
      }),
    ]
    await Promise.allSettled(promises)
  },
  
  // Prefetch member-related data
  memberData: async () => {
    const promises = [
      queryClient.prefetchQuery({
        queryKey: queryKeys.members,
        ...cacheConfig.semiStatic,
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.memberStats,
        ...cacheConfig.semiStatic,
      }),
    ]
    await Promise.allSettled(promises)
  },
  
  // Prefetch calendar data for current month
  calendarData: async (start: string, end: string) => {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.calendarBookingsByRange(start, end),
      ...cacheConfig.dynamic,
    })
  },
}

// Cache invalidation patterns
export const invalidateQueries = {
  // Invalidate all booking-related queries
  bookings: () => {
    queryClient.invalidateQueries({ queryKey: ['bookings'] })
    queryClient.invalidateQueries({ queryKey: ['calendar'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  },
  
  // Invalidate member-related queries
  members: () => {
    queryClient.invalidateQueries({ queryKey: ['members'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  },
  
  // Invalidate payment-related queries
  payments: () => {
    queryClient.invalidateQueries({ queryKey: ['payments'] })
    queryClient.invalidateQueries({ queryKey: ['invoices'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['reports'] })
  },
  
  // Invalidate invoice-related queries
  invoices: () => {
    queryClient.invalidateQueries({ queryKey: ['invoices'] })
    queryClient.invalidateQueries({ queryKey: ['payments'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['reports'] })
  },
  
  // Invalidate facility-related queries
  facilities: () => {
    queryClient.invalidateQueries({ queryKey: ['facilities'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  },
  
  // Invalidate all dashboard queries
  dashboard: () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  },
}

// Background sync for real-time data
export const backgroundSync = {
  // Start background sync for dashboard
  startDashboardSync: () => {
    const interval = setInterval(() => {
      queryClient.refetchQueries({ 
        queryKey: queryKeys.dashboardStats,
        type: 'active'
      })
    }, 60000) // Every minute
    
    return () => clearInterval(interval)
  },
  
  // Start background sync for calendar
  startCalendarSync: () => {
    const interval = setInterval(() => {
      queryClient.refetchQueries({ 
        queryKey: ['calendar'],
        type: 'active'
      })
    }, 30000) // Every 30 seconds
    
    return () => clearInterval(interval)
  },
}

// Optimistic update utilities
export const optimisticUpdates = {
  // Optimistically update booking status
  updateBookingStatus: (bookingId: string, newStatus: string) => {
    queryClient.setQueryData(
      queryKeys.booking(bookingId),
      (old: Record<string, unknown> | undefined) => old ? { ...old, status: newStatus } : old
    )

    // Update in calendar view as well
    queryClient.setQueriesData(
      { queryKey: ['calendar'] },
      (old: Array<{ id: string; status: string; [key: string]: unknown }> | undefined) => {
        if (!old) return old
        return old.map((booking) =>
          booking.id === bookingId
            ? { ...booking, status: newStatus }
            : booking
        )
      }
    )
  },

  // Optimistically update payment status
  updatePaymentStatus: (paymentId: string, newStatus: string) => {
    queryClient.setQueryData(
      queryKeys.payment(paymentId),
      (old: Record<string, unknown> | undefined) => old ? { ...old, payment_status: newStatus } : old
    )
  },
}